import { LogParams } from 'types/getParams';

import { serializeForLaravel } from '@utils/serializeForLaravel';

import { CRMAPIBase } from '../crmApiBase';
import { CRMListResp } from '../responseModels/crm/crmListResponse';
import { CRMResp } from '../responseModels/crm/crmResponse';
import { CRMTotalResp } from '../responseModels/crm/crmTotalResponse';
import { RequestResult } from '../responseModels/requestResult';

// Определяем интерфейс для параметров обновления CRM
interface ICrmUpdateFields {
    organization_name: string;
    description: string;
    address: string;
    crm_id: number;
}

// Тип для поля категории
interface CategoryField {
    id: number;
    name: string;
}

// Тип для подкатегории (упрощенная структура)
export interface SubCategory {
    id: number;
    name: string;
}

// Тип для категории деятельности
export interface ActivityCategory {
    id: number;
    name: string;
    fields: CategoryField[];
    subcategories: SubCategory[];
    parent_id: number;
}

// Тип для метаданных пагинации
interface PaginationLink {
    url: string | null;
    label: string;
    active: boolean;
}

// Тип для метаданных ответа
interface ActivityCategoriesMeta {
    current_page: number;
    from: number;
    last_page: number;
    links: PaginationLink[];
    path: string;
    per_page: number;
    to: number;
    total: number;
}

// Тип для ссылок пагинации
interface ActivityCategoriesLinks {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
}

// Тип для ответа API с категориями деятельности
export interface ActivityCategoriesResponse {
    data: ActivityCategory[];
    links: ActivityCategoriesLinks;
    meta: ActivityCategoriesMeta;
}

// Тип для ответа API с категориями деятельности
export interface ActivityCategoryResponse {
    data: ActivityCategory;
}

// Родитель категории из топа (укороченная форма — только id и name)
export interface TopActivityCategoryParent {
    id: number;
    name: string;
}

// Элемент топа категорий деятельности
export interface TopActivityCategory {
    id: number;
    name: string;
    parent_id: number | null;
    usage_count: number;
    parents: TopActivityCategoryParent[];
}

// Ответ GET activity-categories/top
export interface ActivityCategoriesTopResponse {
    data: TopActivityCategory[];
}

class CRMGroup extends CRMAPIBase {
    /**
     * Добавление CRM
     * @param name Название
     * @param owner_id ID создателя
     * @param description Описание
     * @returns Результат запроса
     */
    async saveCRM(payload): Promise<RequestResult<CRMResp>> {
        const result = await this.post<CRMResp>('/crms', payload);
        return result;
    }

    /**
     * Получение CRM
     * @param crmId ID CRM
     * @returns Результат запроса
     */
    async getCRM(crmId: number): Promise<RequestResult<CRMResp>> {
        const result = await this.get<CRMResp>(`/crms/${crmId}`, { crm_id: crmId });
        return result;
    }

    /**
     * Получение всех CRM
     * @returns Результат запроса
     */
    async getCRMList(is_worker: boolean): Promise<RequestResult<CRMListResp>> {
        const result = await this.get<CRMListResp>('/crms', { is_worker: is_worker });
        return result;
    }

    /**
     * Обновление CRM
     * @param crmId ID CRM
     * @param updateFields Поля для обновления
     * @returns Результат запроса
     */
    async updateCRM(
        crmId: number,
        updateFields: ICrmUpdateFields
    ): Promise<RequestResult<CRMResp>> {
        const result = await this.put<CRMResp>(`/crms/${crmId}`, {
            crm_id: crmId,
            ...updateFields,
        });
        return result;
    }

    /**
     * Удаление CRM
     * @param crmId ID CRM
     * @returns Результат запроса
     */
    async removeCRM(crmId: number): Promise<RequestResult<CRMResp>> {
        const result = await this.delete<CRMResp>(`/crms/${crmId}`, { crm_id: crmId });
        return result;
    }

    /**
     * @deprecated
     * Поиск CRM
     * @param organizationName Название CRM
     * @returns Результат запроса
     */
    async searchCRM(organizationName: string): Promise<RequestResult<CRMListResp>> {
        const result = await this.get<CRMListResp>('/crm/search', {
            query: 'organization_name=' + organizationName,
        });
        return result;
    }

    /**
     * Статистика CRM
     * @param crmId ID CRM
     * @returns Результат запроса
     */
    async getCRMTotal(crmId: number): Promise<RequestResult<CRMTotalResp>> {
        const result = await this.get<CRMTotalResp>(`/crms/${crmId}/total`, { crm_id: crmId });
        return result;
    }

    /**
     * Получение списка логов CRM
     * @param crmId ID CRM
     * @param subject_type Тип объекта
     * @param filters.created_at Период создания
     * @param page Страница
     * @param per_page Записей на страницу
     * @returns Результат запроса
     */
    async getLogList(params: LogParams): Promise<RequestResult<any>> {
        const result = await this.get<any>('/logs', { ...params });
        return result;
    }

    /**
     * Получение категорий деятельности
     * @param page Номер страницы
     * @returns Результат запроса с категориями деятельности
     */
    async getCRMCategories(
        page = 1,
        only_parents = true
    ): Promise<RequestResult<ActivityCategoriesResponse>> {
        const result = await this.get<ActivityCategoriesResponse>(`activity-categories`, {
            page,
            only_parents,
        });
        return result;
    }

    /**
     * Получение топа категорий деятельности
     */
    async getCRMCategoriesTop(params: {
        crm_id: number | string;
        type: 'product' | 'object' | 'service';
    }): Promise<RequestResult<ActivityCategoriesTopResponse>> {
        const result = await this.get<ActivityCategoriesTopResponse>('activity-categories/top', {
            ...params,
        });
        return result;
    }

    /**
     * Получение категории деятельности по ID
     */
    async getCRMCategory({
        categoryId,
        isWithValues = false,
        selectedFields = null,
        type,
        crmId,
    }: {
        categoryId: number | string;
        isWithValues?: boolean;
        selectedFields?: {
            name: string;
            value: string;
        }[];
        type?: 'product' | 'baseProduct' | 'object';
        crmId?: number | string;
    }): Promise<RequestResult<ActivityCategoryResponse>> {
        const rawParams: Record<string, any> = {};
        if (type) {
            rawParams.type = type;
        }
        if (crmId !== undefined && crmId !== null) {
            rawParams.crm_id = crmId;
        }
        if (isWithValues) {
            rawParams.mode = 'with_values';
        }

        if (selectedFields) {
            rawParams.selected_fields = selectedFields;
        }

        const params = serializeForLaravel(rawParams);

        const result = await this.get<ActivityCategoryResponse>(
            `admin/activity-categories/${categoryId}`,
            params
        );
        return result;
    }
    /**
     * Создание категории деятельности
     * @param payload Объект с названием и ID родителя
     */
    async createCRMCategory(params: {
        name: string;
        value: string;
        parent_id?: number | null;
    }): Promise<RequestResult<ActivityCategoryResponse>> {
        const result = await this.post<ActivityCategoryResponse>(
            'admin/activity-categories',
            params
        );
        return result;
    }

    /**
     * Обновление категории деятельности
     * @param categoryId ID категории
     * @param payload Объект с полями для обновления
     */
    async updateCRMCategory(params: {
        category_id: number;
        name?: string;
        value: string;
        parent_id?: number | null;
    }): Promise<RequestResult<ActivityCategoryResponse>> {
        const { category_id, ...data } = params;
        const result = await this.put<ActivityCategoryResponse>(
            `admin/activity-categories/${category_id}`,
            data
        );
        return result;
    }

    /**
     * Удаление категории деятельности
     * @param categoryId ID категории
     */
    async deleteCRMCategory(params: { category_id: number }): Promise<RequestResult<void>> {
        const result = await this.delete<void>(`admin/activity-categories/${params.category_id}`);
        return result;
    }
}

export { CRMGroup };
